import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { getCountryDataList } from 'countries-list';

/**
 * Document-country picker for a `license_verification` field.
 *
 * Jumio only accepts an ISO 3166-1 alpha-3 code and answers anything else
 * ("GB", "UK", "GBR " with a trailing space) with a bare 400 that names no
 * field — which surfaced as "Could not start verification" on the driver's
 * phone. A picker makes an invalid value impossible to type; the backend checks
 * it again on save and at scan time.
 */

export interface JumioCountry {
  iso3: string;
  iso2: string;
  name: string;
}

export const JUMIO_COUNTRIES: JumioCountry[] = getCountryDataList()
  .map((c) => ({ iso3: c.iso3.toUpperCase(), iso2: c.iso2.toUpperCase(), name: c.name }))
  .sort((a, b) => a.name.localeCompare(b.name));

const BY_ISO3 = new Map(JUMIO_COUNTRIES.map((c) => [c.iso3, c]));
const BY_ISO2 = new Map(JUMIO_COUNTRIES.map((c) => [c.iso2, c]));

/**
 * Same rules as the backend's normalizeJumioCountry: blank is "any country",
 * ISO-3 is trimmed and upper-cased, a valid ISO-2 code maps to its ISO-3 twin,
 * anything else is invalid.
 */
export const normalizeJumioCountry = (raw: unknown): { country: string; valid: boolean } => {
  const code = raw == null ? '' : String(raw).trim().toUpperCase();
  if (!code) return { country: '', valid: true };
  if (BY_ISO3.has(code)) return { country: code, valid: true };
  const mapped = BY_ISO2.get(code);
  if (mapped) return { country: mapped.iso3, valid: true };
  return { country: code, valid: false };
};

interface JumioCountrySelectProps {
  /** Stored ISO-3 code, or '' for any country. */
  value?: string | null;
  onChange: (iso3: string) => void;
  size?: 'small' | 'medium';
  sx?: object;
  helperText?: React.ReactNode;
}

const JumioCountrySelect: React.FC<JumioCountrySelectProps> = ({ value, onChange, size = 'small', sx, helperText }) => {
  const { country, valid } = normalizeJumioCountry(value);
  const selected = valid && country ? BY_ISO3.get(country) ?? null : null;

  return (
    <Autocomplete
      size={size}
      sx={sx}
      options={JUMIO_COUNTRIES}
      value={selected}
      onChange={(_, option) => onChange(option?.iso3 ?? '')}
      getOptionLabel={(o) => `${o.name} (${o.iso3})`}
      isOptionEqualToValue={(o, v) => o.iso3 === v.iso3}
      filterOptions={(options, { inputValue }) => {
        const q = inputValue.trim().toLowerCase();
        if (!q) return options;
        return options.filter(
          (o) => o.name.toLowerCase().includes(q) || o.iso3.toLowerCase() === q || o.iso2.toLowerCase() === q
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Document country (ISO-3)"
          placeholder="Any country"
          error={!valid}
          helperText={!valid ? `"${String(value)}" is not a valid country code — pick one from the list or clear it.` : helperText}
        />
      )}
    />
  );
};

export default JumioCountrySelect;
